"use client";

import React from "react";
import Table, { Column } from "../ui/Table";
import type { ZeroDay } from "@/lib/zeroDayTracker";

interface ZeroDayTrackerProps {
  /** Rows from the research team's sheet, newest first (src/lib/zeroDayTracker.ts). */
  data: ZeroDay[];
}

/**
 * Text colour per known severity; anything else shows as typed, uncoloured.
 * Maps, not object literals: the keys come from free text in a sheet, and a
 * cell reading "constructor" would otherwise find Object.prototype's.
 */
const SEVERITY_COLOR = new Map([
  ["critical", "text-status-critical"],
  ["high", "text-status-high"],
  ["medium", "text-status-medium"],
  ["low", "text-secondary"],
]);

/** Text colour per known status; anything else shows as typed, uncoloured. */
const STATUS_COLOR = new Map([
  ["killed", "text-secondary"],
  ["active", "text-status-critical"],
]);

const ZeroDayTracker = ({ data }: ZeroDayTrackerProps) => {
  const columns: Record<string, Column<ZeroDay>> = {
    date: {
      id: "date",
      name: "Date",
      show: true,
      width: 145,
      render: (rowData: ZeroDay) => <span>{rowData.date}</span>,
    },
    product: {
      id: "product",
      name: "Product",
      show: true,
    },
    versions: {
      id: "versions",
      name: "Versions",
      show: true,
      width: 177,
    },
    type: {
      id: "type",
      name: "Type",
      show: true,
      width: 270,
    },
    severity: {
      id: "severity",
      name: "Severity",
      show: true,
      width: 130,
      align: "center" as const,
      render: (rowData: ZeroDay) => (
        <span
          className={`font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] capitalize ${
            SEVERITY_COLOR.get(rowData.severity.toLowerCase()) ?? "text-text-default"
          }`}
        >
          {rowData.severity}
        </span>
      ),
    },
    status: {
      id: "status",
      name: "Status",
      show: true,
      width: 198,
      align: "center" as const,
      render: (rowData: ZeroDay) => (
        <span
          className={`font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] ${
            STATUS_COLOR.get(rowData.status.toLowerCase()) ?? "text-text-default"
          }`}
        >
          {rowData.status}
        </span>
      ),
      collapseContent: (rowData: ZeroDay) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 py-2">
          <div className="flex gap-4">
            <span className="text-primary font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] min-w-[100px]">
              Vendor:
            </span>
            <span className="text-white font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px]">
              {rowData.details.vendor || "-"}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-primary font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] min-w-[100px]">
              Patch:
            </span>
            <span className="text-white font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px]">
              {rowData.details.patch || "-"}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-primary font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] min-w-[100px]">
              Platform:
            </span>
            <span className="text-white font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px]">
              {rowData.details.platform || "-"}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-primary font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] min-w-[100px]">
              Summary:
            </span>
            <span className="text-white font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px]">
              {rowData.details.summary || "-"}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-primary font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] min-w-[100px]">
              CVSS Score:
            </span>
            <span className="text-white font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px]">
              {rowData.details.cvssScore || "-"}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-primary font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] min-w-[100px]">
              Credits:
            </span>
            <span className="text-white font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px]">
              {rowData.details.credits || "-"}
            </span>
          </div>
        </div>
      ),
    },
  };

  return (
    <div className="relative w-full px-4 sm:px-6 lg:px-8 pt-2 pb-12 overflow-hidden">
      <div className="max-w-[1300px] mx-auto relative z-10">
        {/* Description Section */}
        <div className="mb-12 max-w-[644px] space-y-6 sm:space-y-7">
          <p className="body-1 text-text-default">
            Zero-Day Tracker is built to help you understand a fully patched system and application
            cannot guarantee security. All the vulnerabilities listed below are the results of our
            researches and they have not been disclosed to the vendors or public. Though in the past
            we have clearly supported responsible disclosure of security vulnerability, now we no
            longer endorse it.
          </p>
          <p className="body-1 text-text-default">
            While we are trying to keep the information as accurate as possible, we cannot guarantee
            that these vulnerabilities are indeed zero-day since it is a known fact that they may
            have been circled around among the hackers for a long time.
          </p>
          <p className="body-1 text-text-default">
            The list below covers some of the vulnerabilities that we have in our lab and this list
            will be updated when we see fit.
          </p>
        </div>

        {/* Table */}
        <Table columns={columns} data={data} enableCollapse={true} customClassName="" />
      </div>
    </div>
  );
};

export default ZeroDayTracker;
